import { useMemo, useRef, useState } from 'react';
import './SimulationTimeframe.scss'
import useUrlParams from '../../../../hooks/useUrlParams';
import useScenario from '../../../../hooks/useScenario';
import { isOperateMode } from '../../../../hooks/utils';
import { TimeFrameInMinutes, TimeFrameOptions } from '../../../../../CommonComponents/timeline/constant';
import {
  OrbitIndicatorsContainer,
  RealtimeTracker,
  Timeline,
  TimelineBodyContainer,
  TimelineContainer,
  TimelineContainerDevider,
  TimelineHeader,
  TimelineScrollContainer,
  TimelineTrack,
  TimelineTrackContainer
} from '../../../../../CommonComponents/timeline/Timeline';
import { ScheduleIconSvg } from '../../../../../CommonComponents/svgs/Svgs';
import useViewer from '../../../../hooks/useViewer';
import { useActivePointOnScroll } from '../../../../../CommonComponents/timeline/hooks/useActivePointProps';
import { useScrollBoundary } from '../../../../../CommonComponents/timeline/hooks/useScrollBoundary';
import { getNextTimeFrame } from '../../../../../CommonComponents/timeline/utils';
import OnboardContact from './OnboardContact';
import FaultEvents from './FaultEvents';
import { GroundScheduleSvg } from '../../../header/Svgs';
import GroundContact from './GroundContact';
import OrbitAnimationTimeline from './OrbitAnimationTimeline';
import ContactTimelineHeader from '../../../../custom_components/command-center-timeline/ContactTimelineHeader';
import useLoader from '../../../../hooks/useLoader';
import useTimeframe from '../../../../hooks/useTimeframe';
import { ButtonSvg } from './Svgs';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import ContactAlert from './ContactAlert';

const SimulationTimeframe = () => {
  const { loading } = useLoader()
  const { dashboard } = useUrlParams()
  const { scenario } = useScenario();
  const { change_viewer_time } = useViewer();
  const {
    simulation_start_time,
    simulation_end_time,
    simulation_duration,
    current_time,
    showFrameButton,
    getUpdatedFrame,
    setShowFrameButton } = useTimeframe();

  const operateMode = isOperateMode(dashboard, scenario)

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [selectedOrbit, setSelectedOrbit] = useState('Orbit 1');
  const [selectedFrame, setSelectedFrame] = useState(TimeFrameOptions[0]);

  const frameStartTime = useMemo(() => operateMode ? simulation_start_time : scenario?.scenarioStartTime || 0, [scenario, operateMode, simulation_start_time])
  const frameDuration = useMemo(() => operateMode ? simulation_duration : scenario?.scenarioDuration || 0, [scenario, operateMode, simulation_duration])
  const frameEndTime = useMemo(() => operateMode ? simulation_end_time : (frameStartTime + (frameDuration * 60)), [scenario, operateMode, simulation_end_time, frameStartTime, frameDuration])

  const onOrbitChange = (orbit: string) => {
    const orbitIndex = Number(orbit.replace(/\D/g, "") || 1)
    const selectedOrbit = `orbit_${orbitIndex}`
    const orbitPosition = document.getElementById(selectedOrbit)
    if (!scrollAreaRef.current || !orbitPosition) return
    scrollAreaRef.current.scrollLeft = orbitPosition.offsetLeft
    setSelectedOrbit(orbit);
  }

  useActivePointOnScroll({
    scrollAreaRef: scrollAreaRef,
    pointSelector: ".orbit__point",
    selectedFrame: selectedFrame,
    onActiveChange: (visiblePoints) => {
      if (!visiblePoints || visiblePoints.length === 0) return;
      // visiblePoints are sorted by left; first and last define the visible range
      if (visiblePoints.length === 1) {
        const id = visiblePoints[0].id; // e.g. 'orbit_1'
        const numMatch = id.match(/\d+/);
        const num = numMatch ? numMatch[0] : '';
        if (!num) return;
        setSelectedOrbit(`Orbit ${num}`);
        return;
      }

      const firstId = visiblePoints[0].id;
      const lastId = visiblePoints[visiblePoints.length - 1].id;
      const firstMatch = firstId.match(/\d+/);
      const lastMatch = lastId.match(/\d+/);
      if (!firstMatch || !lastMatch) return;
      const firstNum = firstMatch[0];
      const lastNum = lastMatch[0];
      // Range display: Orbit X-Y
      setSelectedOrbit(`Orbit ${firstNum}-${lastNum}`);
    }
  });

  useScrollBoundary({
    scrollAreaRef: scrollAreaRef,
    direction: "horizontal",
    onBoundaryChange: (boundary) => {
      if (!scenario || !operateMode) return
      setShowFrameButton(boundary)
    },
    onVerticalScroll: (scroll) => {
      setSelectedFrame((prev) => {
        // map current label -> minutes -> next minutes -> label
        const currentMinutes = TimeFrameInMinutes[prev as keyof typeof TimeFrameInMinutes] ?? TimeFrameInMinutes[TimeFrameOptions[0]]
        const nextMinutes = getNextTimeFrame(currentMinutes, scroll)
        const nextOption = TimeFrameOptions.find(option => TimeFrameInMinutes[option as keyof typeof TimeFrameInMinutes] === nextMinutes)
        return nextOption ?? prev
      })
    }
  });

  const onSelectFrame = (frame: string) => {
    const value = TimeFrameInMinutes[frame]
    setSelectedFrame(frame)
    if ([30, 60].includes(value) && selectedOrbit.includes('-') && scrollAreaRef.current) {
      scrollAreaRef.current.scrollLeft = 0
      setSelectedOrbit('Orbit 1')
    }
  }

  if (!current_time) return null

  return (
    <div className={`contact__center__container__wrapper`}>
      {showFrameButton &&
        <button className={`update__frame__button  
        ${showFrameButton === 'start' ? 'left' : 'right'}`}
          onClick={() => getUpdatedFrame(showFrameButton)}
        >
          <CustomToolTip placement={'top'} title={showFrameButton === 'start' ? 'Jump to previous' : 'Jump to next'}>
            <ButtonSvg />
          </CustomToolTip>
        </button>
      }
      <OrbitAnimationTimeline />
      {operateMode && <ContactAlert />}
      <Timeline
        scrollAreaRef={scrollAreaRef}
        timelineDuration={frameDuration}
        timelineStartTime={frameStartTime}
        timelineEndTime={frameEndTime}
        selectedFrame={TimeFrameInMinutes[selectedFrame]}
        loading={loading}
      >
        <TimelineHeader>
          <ContactTimelineHeader
            Orbits={[]}
            onOrbitChange={onOrbitChange}
            selectedOrbit={selectedOrbit}
            selectedFrame={selectedFrame}
            onTimeFrameChange={onSelectFrame}
          />
        </TimelineHeader>
        <TimelineBodyContainer>
          <TimelineScrollContainer>
            <TimelineContainer className='z-9'>
              <TimelineTrackContainer>
                <TimelineTrack />
                <RealtimeTracker
                  strtTime={simulation_start_time}
                  endTime={simulation_end_time}
                  duration={simulation_duration}
                  onChange={change_viewer_time}
                  currentTime={current_time}
                  disabled={dashboard !== 'summary'}
                />
                {/* <SunExposers /> */}
                <OrbitIndicatorsContainer />
                <FaultEvents />
              </TimelineTrackContainer>
            </TimelineContainer>
            <TimelineContainer
              sidebarDetails={{
                name: 'Ground Contacts',
                icon: <GroundScheduleSvg />
              }}
            >
              <GroundContact />
            </TimelineContainer>
            <TimelineContainerDevider />
            <TimelineContainer
              sidebarDetails={{
                name: 'Onboard Schedule',
                icon: <ScheduleIconSvg />
              }}
            >
              <OnboardContact />
            </TimelineContainer>
          </TimelineScrollContainer>
        </TimelineBodyContainer>
      </Timeline>
    </div>
  )
}

export default SimulationTimeframe